import React from "react";

import { FormattedMessage } from "react-intl";

import Heading from "@atlaskit/heading";
import Link from "@atlaskit/link";
import {
    Box,
    xcss,
    Inline,
    Stack,
    Text,
    TextColor
} from "@atlaskit/primitives";
import Skeleton from "@atlaskit/skeleton";

import { messages } from "./messages";

const boxStyles = xcss({
    borderRadius: "border.radius.100",
    borderColor: "color.border",
    borderWidth: "border.width",
    borderStyle: "solid",
    color: "color.text",
    backgroundColor: "color.background.neutral.subtle"
});

const stackStyles = xcss({
    height: "40px"
});

interface SummaryCardInterface {
    value?: number;
    title: string;
    subtitle?: React.ReactNode;
    valueColor?: TextColor;
    isLoading?: boolean;
    hasError?: boolean;
}

export const SummaryCard: React.FC<SummaryCardInterface> = ({
    value = 0,
    title,
    subtitle,
    valueColor = "color.text",
    isLoading = false,
    hasError = false
}) => {
    const valueStyles = xcss({
        color: valueColor
    });

    return (
        <Box padding="space.150" xcss={boxStyles}>
            <Inline space="space.200" alignBlock="center">
                {isLoading && (
                    <Stack
                        space="space.050"
                        alignBlock="center"
                        xcss={stackStyles}
                    >
                        <Skeleton
                            width="200px"
                            height="16px"
                            testId="summary-skeleton"
                            isShimmering
                        />
                        <Skeleton
                            width="125px"
                            height="16px"
                            testId="summary-skeleton"
                            isShimmering
                        />
                    </Stack>
                )}
                {!isLoading && hasError && (
                    <Stack alignBlock="center" xcss={stackStyles}>
                        <Heading size="xsmall">
                            <FormattedMessage {...messages.errorTitle} />
                        </Heading>
                        <Link href="/fastshift/dashboard">
                            <FormattedMessage {...messages.errorDescription} />
                        </Link>
                    </Stack>
                )}
                {!isLoading && !hasError && (
                    <>
                        <Heading size="xxlarge">
                            <Box xcss={valueStyles}>{value}</Box>
                        </Heading>
                        <Box>
                            <Stack alignBlock="center">
                                <Heading size="xxsmall">{title}</Heading>
                                {subtitle !== undefined && (
                                    <Text color="color.text.subtlest">
                                        {subtitle}
                                    </Text>
                                )}
                            </Stack>
                        </Box>
                    </>
                )}
            </Inline>
        </Box>
    );
};
